/* eslint-disable no-param-reassign */
import produce from 'immer';

import { convertNumToCurrencyStr, formatDate, ordinalize } from '../../../utils/globalHelpers';
import { AUTOPAY_MODAL_DICTIONARY, REASON_CODE_DICTIONARY } from './constants';

/**
 * Check if reasons array has any full error reason codes, order of arrays does not matter
 */
export const checkFullErrorReasonCodes = (arr1, arr2) => arr1.some((val) => arr2.includes(val));

/**
 * formatAutopayStatusProperties
 * @param {object} currentSetting
 * @param {number} currentSetting.delayDays integer representing numDays after 1st of month that
 *                                          payment will be scheduled
 * @param {number} currentSetting.totalMonthlyAmount number (w/2 decimals)
 * @returns object with formatted (ordinalized) scheduledDate and totalMonthlyPayment formatted like
 *          "1st" and "$1,234.56". Returns null if currentSetting is falsy.
 * @example
 *  null -> null
 * @example
 *  { delayDays: 2, totalMonthlyPayment: 1234.56 }
 *    ->
 *  { scheduledDate: "3rd", totalMonthlyPayment: "$1,234.56" }
 */

export const formatAutopayStatusProperties = (currentSetting) => {
  if (!currentSetting) return null;
  const {
    cancellableDate,
    delayDays,
    nextDraftDate,
    totalMonthlyPayment,
  } = currentSetting;
  const formattedAmount = convertNumToCurrencyStr(totalMonthlyPayment);
  const formattedCancellableDate = formatDate(cancellableDate, 'UTC', 'LL');
  const formattedNextDraftDate = formatDate(nextDraftDate, 'UTC', 'LL');
  const ordinalizedScheduledDate = ordinalize(1 + delayDays);

  return {
    cancellableDate: formattedCancellableDate,
    nextDraftDate: formattedNextDraftDate,
    scheduledDate: ordinalizedScheduledDate,
    totalMonthlyPayment: formattedAmount,
  };
};

/**
 * Extracts and parses existingAccountInfo from autopay currentSetting
 * @param {object} autopayStatusResponse.currentSetting
 */
export const getAutopayExistingAccountInfo = ({
  isSavedSavings,
  maskedAccountNumber,
  maskedNameOnAccount,
  maskedRoutingNumber,
}) => ({
  accountType: isSavedSavings ? 'Saving' : 'Checking',
  maskedAccountNumber,
  maskedNameOnAccount,
  maskedRoutingNumber,
});

export const generateCancelAutopayModalProps = ({
  autopayStatus,
  dispatchCancelAutopay,
  dispatchSetIsModalOpen,
  ...restProps
}) => {
  const {
    label = '',
    text = '',
    title = '',
  } = AUTOPAY_MODAL_DICTIONARY.cancel[autopayStatus] || {};

  return ({
    handleCloseModal: () => dispatchSetIsModalOpen({ key: 'cancel', value: false }),
    handleOnPress: dispatchCancelAutopay,
    label,
    text,
    title,
    ...restProps,
  });
};

export const getDelayDaysString =
  (delayDays) => `${delayDays} day${delayDays !== 1 ? 's' : ''} after due date`;

export const parseReasonCodes = (reasons, defaultErrors) => {
  const {
    accountNumber,
    additionalPrincipal,
    bannerError,
    beginDate,
    delayDays,
    nameOnAccount,
    routingNumber,
    sourcesBannerError,
  } = produce(defaultErrors, (draft) => {
    reasons.forEach((reasonCode) => {
      if (REASON_CODE_DICTIONARY[reasonCode]) {
        const { key, message } = REASON_CODE_DICTIONARY[reasonCode];
        draft[key] = message;
      }
    });
  });
  return {
    autopay: {
      account: {
        beginDate,
        delayDays,
      },
      amount: { additionalPrincipal },
      bannerError,
    },
    sources: {
      account: {
        accountNumber,
        nameOnAccount,
        routingNumber,
      },
      bannerError: sourcesBannerError,
    },
  };
};
